import find from "lodash-es/find"
import flatMap from "lodash-es/flatMap"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import type { TFreeResponsesQuestion } from "@/3514/entities/free-responses"
import type { TLikertQuestion, TLikertQuestionOption } from "@/3514/entities/likert"
import type {
    ISelfDiagnosticQuestionOptionModel as IDiagnosticOptionModel,
    IReflectionQuestionBaseModel as IReflectionQuestionModel
} from "@/3514/types"
import type { TGetValueFromTranslatedObjectFunction, TTFunction } from "@/hooks"

import type {
    IDiagnosticSummaryDiagnosticAnswerModel as IDiagnosticAnswer,
    IDiagnosticSummaryDiagnosticModel as IDiagnosticModel,
    IDiagnosticSummaryDiagnosticQuestionModel as IDiagnosticQuestionModel,
    IDiagnosticSummaryQueryModel as IQueryModel,
    IDiagnosticSummaryReflectionAnswerModel as IReflectionAnswerModel,
    IDiagnosticSummaryReflectionModel as IReflectionModel
} from "./diagnostic-summary.api.types"

function freeResponsesMapper(
    queryModel: IQueryModel,
    { t, translator }: { t: TTFunction; translator: TGetValueFromTranslatedObjectFunction }
): TFreeResponsesQuestion[] {
    if (isEmpty(queryModel)) return []

    if (isEmpty(queryModel.reflections)) return []

    const reflections: IReflectionModel[] = flatMap<IReflectionModel>(queryModel.reflections)

    const reflectionAnswers: IReflectionAnswerModel[] = flatMap<IReflectionModel, IReflectionAnswerModel>(
        reflections,
        (r: IReflectionModel): IReflectionAnswerModel[] => r.answers
    )

    const reflectionQuestions: IReflectionQuestionModel[] = flatMap<IReflectionModel, IReflectionQuestionModel>(
        reflections,
        (r: IReflectionModel): IReflectionQuestionModel[] => r.questions
    )

    return map<IReflectionQuestionModel, TFreeResponsesQuestion>(
        reflectionQuestions,
        (q: IReflectionQuestionModel): TFreeResponsesQuestion => {
            const answer: IReflectionAnswerModel = find<IReflectionAnswerModel>(reflectionAnswers, {
                questionUuid: q.uuid
            })

            return {
                initialState: {
                    answer: String(""),
                    id: q.uuid,
                    question: translator(q.question),
                    placeholder: ((): string => {
                        if (isEmpty(answer))
                            return translator(q.prompt) || t("entities.diagnosticSummary.emptyAnswer.placeholder")

                        return undefined
                    })(),
                    isRequired: false
                },
                submittedState: isEmpty(answer)
                    ? null
                    : {
                          id: answer.questionUuid,
                          answer: answer.answer,
                          question: translator(q.question)
                      }
            }
        }
    )
}

function likertQuestionsMapper(
    diagnostics: IDiagnosticModel[],
    translator: TGetValueFromTranslatedObjectFunction
): TLikertQuestion[] {
    return isEmpty(diagnostics)
        ? []
        : ((): TLikertQuestion[] => {
              const flatQuestions: IDiagnosticQuestionModel[] = flatMap<IDiagnosticModel, IDiagnosticQuestionModel>(
                  diagnostics,
                  (d: IDiagnosticModel): IDiagnosticQuestionModel[] => d.questions
              )

              const flatAnswers: IDiagnosticAnswer[] = flatMap<IDiagnosticModel, IDiagnosticAnswer>(
                  diagnostics,
                  (d: IDiagnosticModel): IDiagnosticAnswer[] => d.answers
              )

              return map<IDiagnosticQuestionModel, TLikertQuestion>(
                  flatQuestions,
                  (model: IDiagnosticQuestionModel): TLikertQuestion => {
                      const answeredQuestion: IDiagnosticAnswer = find<IDiagnosticAnswer>(flatAnswers, {
                          questionUuid: model.uuid
                      })

                      return {
                          initialState: {
                              id: model.uuid,
                              content: translator(model.question),
                              order: model.order,
                              isNotApplicable: false,
                              notApplicableLabel: String(""),
                              notApplicablePrompt: String(""),
                              options: isEmpty(model.options)
                                  ? []
                                  : map<IDiagnosticOptionModel, TLikertQuestionOption>(
                                        model.options,
                                        (o: IDiagnosticOptionModel, index: number): TLikertQuestionOption => ({
                                            index,
                                            uuid: o.uuid,
                                            label: translator(o.response),
                                            isFreeResponseAllowed: false,
                                            freeResponsePrompt: undefined,
                                            freeResponsePlaceholder: undefined,
                                            wasSelected: false
                                        })
                                    )
                          },
                          submittedState: isEmpty(answeredQuestion)
                              ? null
                              : {
                                    id: answeredQuestion.questionUuid,
                                    isNotApplicable: false,
                                    freeResponseValue: String(""),
                                    wasOptionSelected: !isEmpty(answeredQuestion.optionUuid),
                                    options: isEmpty(model.options)
                                        ? []
                                        : map<IDiagnosticOptionModel, TLikertQuestionOption>(
                                              model.options,
                                              (o: IDiagnosticOptionModel, index: number): TLikertQuestionOption =>
                                                  o.uuid === answeredQuestion.optionUuid
                                                      ? {
                                                            index,
                                                            uuid: answeredQuestion.optionUuid,
                                                            wasSelected: true,
                                                            label: translator(o.response),
                                                            isFreeResponseAllowed: false,
                                                            freeResponsePrompt: undefined,
                                                            freeResponsePlaceholder: undefined
                                                        }
                                                      : {
                                                            index,
                                                            uuid: o.uuid,
                                                            wasSelected: false,
                                                            label: translator(o.response),
                                                            isFreeResponseAllowed: false,
                                                            freeResponsePrompt: undefined,
                                                            freeResponsePlaceholder: undefined
                                                        }
                                          )
                                }
                      }
                  }
              )
          })()
}

export default { freeResponsesMapper, likertQuestionsMapper }
